import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { BadgePercent, Check, CircleHelp, Copy, X } from "lucide-react";
import { useGetMarketDataHistory } from "@/api/securities/useGetMarketDataHistory";
import { Button } from "@/components/Button/Button";
import { formatNumber } from "@/utils/number";

export type PriceRow = {
  tier: string;
  note?: string;
  active: string;
  ekb: string;
  ekbNote?: string;
  realEstate: string;
  realEstateNote?: string;
  bonds: string;
  safe: string;
};

export const PERF_FEE_COLS = [
  "Aktiv EKB",
  "Aktiv Eiendom",
  "Aktiv Aksjer",
  "Aktiv 75",
  "Aktiv 60",
  "Aktiv 45",
  "Aktiv 25",
  "Aktiv Renter",
  "Aktiv Trygg",
] as const;

export const PERF_FEE_ROWS = [
  3.7, 4.6, 4.6, 3.85, 3.4, 2.95, 2.45, 1.6, 0.0,
] as const;

export const PRICELIST_ROWS: PriceRow[] = [
  {
    tier: "<2",
    active: "1,2 %",
    ekb: "1,2 %",
    realEstate: "1,2 %",
    bonds: "1,0 %",
    safe: "0,25 %",
  },
  {
    tier: "2-5",
    active: "1 %",
    ekb: "1,1 %",
    realEstate: "1,1 %",
    bonds: "0,8 %",
    safe: "0,25 %",
  },
  {
    tier: "5-10",
    active: "0,9 %",
    ekb: "1,0 %",
    realEstate: "1,0 %",
    bonds: "0,7 %",
    safe: "0,25 %",
  },
  {
    tier: "10+",
    active: "0,8 %",
    ekb: "0,9 %",
    realEstate: "0,9 %",
    bonds: "0,5 %",
    safe: "0,25 %",
  },
  {
    tier: "Minstetegning",
    active: "-",
    ekb: "1 MNOK",
    ekbNote: "5 MNOK i finansiell formue",
    realEstate: "750 000",
    realEstateNote: "7,5 MNOK i finansiell formue",
    bonds: "-",
    safe: "-",
  },
  {
    tier: "Inn/Ut",
    active: "0 %",
    ekb: "0,5 %",
    realEstate: "0,25 %",
    bonds: "0 %",
    safe: "0 %",
  },
  {
    tier: "Kurtasje belastes",
    active: "ja",
    ekb: "nei",
    realEstate: "ja",
    bonds: "ja",
    safe: "ja",
  },
  {
    tier: "Depot",
    note: "0,15 % for aksjesparekonto",
    active: "0,18 %",
    ekb: "0,18 %",
    realEstate: "0,18 %",
    bonds: "0,18 %",
    safe: "0,18 %",
  },
];

const NIBOR_ID = 2_123_649 as const;
const HIGH_WATERMARK_EXPLANATION = `Referanseavkastningen (avkastningskravet porteføljen må oppnå før det beregnes suksesshonorar) er Nibor 3 måneders rente + risikopremie per år. Den beregnes og akkumuleres daglig. Suksesshonorar er 10 % av avkastningen som overstiger referanseavkastningen, og avregnes kvartalsvis.
Vi benytter også et «høyvannsmerke». Det betyr at porteføljens verdi må innhente både eventuelt verdifall og den påløpte referanseavkastningen før det kan beregnes suksesshonorar.

Eksempel:
Kunden investerer 100 kroner. Referanseavkastningen i første periode er 8 %, og porteføljen stiger med 10 %. Suksesshonoraret er 10 % av meravkastningen.

• Porteføljen stiger til 110 kroner: Suksesshonoraret blir (10 − 8) × 10 % = 0,20 kroner. Verdien etter honoraret er 109,80 kroner, som blir utgangspunktet for neste periode.
• Porteføljen faller deretter til 90 kroner: Referanseavkastningen fortsetter å akkumuleres selv om porteføljen faller.
• Porteføljen stiger igjen: Dersom referanseavkastningen frem til neste avregning har utgjort 5 kroner, er det justerte høyvannsmerket 115 kroner, ikke 109,80 kroner. Bare dersom porteføljeverdien på avregningstidspunktet overstiger 115 kroner, beregnes nytt suksesshonorar – og kun av beløpet over dette nivået.

Altså: Nytt suksesshonorar kan først beregnes når verdien overstiger høyvannsmerket og kravet til referanseavkastning er oppfylt.

I praksis beregnes referanseavkastningen daglig, og eventuelt honorar avregnes kvartalsvis`;

export function Pricelist() {
  const [open, setOpen] = useState(false);
  const [highWatermarkOpen, setHighWatermarkOpen] = useState(false);
  const [explanationCopied, setExplanationCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const { data, loading } = useGetMarketDataHistory(NIBOR_ID, new Date());
  const NIBOR = data?.security?.marketDataHistory?.[0]?.close1 ?? 0;

  const renderCellValue = (value: string, isBooleanRow: boolean) => {
    if (!isBooleanRow) {
      const raw = value.replace("%", "").trim().replace(",", ".");
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return `${formatNumber(parsed, 2)} %`;
      }
      return value;
    }

    const normalized = value.trim().toLowerCase();
    if (normalized === "ja") {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
          <Check className="size-4" aria-hidden="true" />
          <span>ja</span>
        </span>
      );
    }

    if (normalized === "nei") {
      return (
        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
          <X className="size-4" aria-hidden="true" />
          <span>nei</span>
        </span>
      );
    }

    return value;
  };

  const copyHighWatermarkExplanation = async () => {
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(HIGH_WATERMARK_EXPLANATION);
      setExplanationCopied(true);
    } catch (error) {
      console.error("Could not copy high-watermark explanation:", error);
      setCopyError("Kunne ikke kopiere forklaringen. Prøv igjen.");
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        icon={<BadgePercent size={16} />}
      >
        Pricelist
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <div className="absolute top-3 right-3">
          <Button variant="icon" size="sm" onClick={() => setOpen(false)}>
            <X className="size-4" />
          </Button>
        </div>
        <DialogTitle className="text-xl">Price List</DialogTitle>
        <DialogContent className="pt-2 flex flex-col gap-4">
          {/* Prisliste */}
          <div className="rounded-2xl border bg-gradient-to-b from-muted/40 to-background p-3 sm:p-4">
            <div className="hidden sm:grid grid-cols-6 gap-2 px-3 py-2 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
              <div>MNOK</div>
              <div className="text-right">Aktiv</div>
              <div className="text-right">EKB</div>
              <div className="text-right">Eiendom</div>
              <div className="text-right">Renter</div>
              <div className="text-right">Trygg</div>
            </div>

            <div className="space-y-2">
              {PRICELIST_ROWS.map((row) => {
                const isBooleanRow = row.tier === "Kurtasje belastes";

                return (
                  <div
                    key={row.tier}
                    className="rounded-xl border border-border/70 bg-background/80 px-3 py-3 sm:py-2 sm:grid sm:grid-cols-6 sm:gap-2 sm:items-center"
                  >
                    <div className="font-semibold text-foreground mb-2 sm:mb-0">
                      <div>{row.tier}</div>
                      {row.note ? (
                        <div className="mt-1 text-xs font-normal text-muted-foreground leading-snug">
                          {row.note}
                        </div>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-2 gap-y-1 text-sm sm:contents">
                      <div className="text-muted-foreground sm:hidden text-right">
                        Aktiv
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        {renderCellValue(row.active, isBooleanRow)}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        EKB
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        <div>{renderCellValue(row.ekb, isBooleanRow)}</div>
                        {row.ekbNote ? (
                          <div className="mt-0.5 text-[12px] font-normal leading-snug text-muted-foreground">
                            {row.ekbNote}
                          </div>
                        ) : null}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        Eiendom
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        <div>
                          {renderCellValue(row.realEstate, isBooleanRow)}
                        </div>
                        {row.realEstateNote ? (
                          <div className="mt-0.5 text-[12px] font-normal leading-snug text-muted-foreground">
                            {row.realEstateNote}
                          </div>
                        ) : null}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        Renter
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        {renderCellValue(row.bonds, isBooleanRow)}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        Trygg
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        {renderCellValue(row.safe, isBooleanRow)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Performance Fee */}
          <div className="rounded-2xl border bg-gradient-to-b from-muted/40 to-background p-3 sm:p-4">
            <div className="mb-3 flex items-center justify-between gap-3 px-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Performance Fee
              </p>
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  setExplanationCopied(false);
                  setCopyError(null);
                  setHighWatermarkOpen(true);
                }}
                icon={<CircleHelp size={14} />}
              >
                Høyvannsmerke
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-separate border-spacing-y-1">
                <thead>
                  <tr>
                    <th className="text-left px-3 pb-1 text-xs uppercase tracking-wide font-semibold text-muted-foreground whitespace-nowrap" />
                    {PERF_FEE_COLS.map((col) => (
                      <th
                        key={col}
                        className="text-right px-3 pb-1 text-xs uppercase tracking-wide font-semibold text-muted-foreground whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* NIBOR 3M — same value across all columns */}
                  <tr className="rounded-xl">
                    <td className="px-3 py-2 font-semibold text-foreground whitespace-nowrap rounded-l-xl bg-background/80 border border-r-0 border-border/70">
                      NIBOR 3M
                    </td>
                    {PERF_FEE_COLS.map((_, index) => (
                      <td
                        key={index}
                        className={
                          "px-3 py-2 text-right tabular-nums font-medium text-foreground bg-background/80 border-y border-border/70" +
                          (index === PERF_FEE_COLS.length - 1
                            ? " rounded-r-xl border-r border-border/70"
                            : "")
                        }
                      >
                        {loading ? "…" : `${formatNumber(NIBOR, 2)} %`}
                      </td>
                    ))}
                  </tr>
                  {/* Risikopremie */}
                  <tr className="rounded-xl">
                    <td className="px-3 py-2 font-semibold text-foreground whitespace-nowrap rounded-l-xl bg-background/80 border border-r-0 border-border/70">
                      Risikopremie
                    </td>
                    {PERF_FEE_ROWS.map((row, index) => (
                      <td
                        key={index}
                        className={
                          "px-3 py-2 text-right tabular-nums font-medium text-foreground bg-background/80 border-y border-border/70" +
                          (index === PERF_FEE_ROWS.length - 1
                            ? " rounded-r-xl border-r border-border/70"
                            : "")
                        }
                      >
                        {formatNumber(row, 2)} %
                      </td>
                    ))}
                  </tr>
                  {/* Hurdle (NIBOR + risikopremie) — highlighted */}
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground whitespace-nowrap rounded-l-xl bg-primary/5 border border-r-0 border-primary/20">
                      Hurdle
                    </td>
                    {PERF_FEE_ROWS.map((row, index) => (
                      <td
                        key={index}
                        className={
                          "px-3 py-2 text-right tabular-nums font-semibold text-foreground bg-primary/5 border-y border-primary/20" +
                          (index === PERF_FEE_ROWS.length - 1
                            ? " rounded-r-xl border-r border-primary/20"
                            : "")
                        }
                      >
                        {loading ? "…" : `${formatNumber(row + NIBOR, 2)} %`}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={highWatermarkOpen}
        onClose={() => setHighWatermarkOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <div className="flex items-center justify-between pr-3">
          <DialogTitle>Høyvannsmerke (High watermark)</DialogTitle>
          <Button
            variant="icon"
            size="sm"
            onClick={() => setHighWatermarkOpen(false)}
            aria-label="Lukk forklaringen"
          >
            <X className="size-4" />
          </Button>
        </div>
        <DialogContent className="space-y-4 text-sm leading-relaxed">
          <p>
            Referanseavkastningen (avkastningskravet porteføljen må oppnå før
            det beregnes suksesshonorar) er Nibor 3 måneders rente +
            risikopremie per år. Den beregnes og akkumuleres daglig.
            Suksesshonorar er 10 % av avkastningen som overstiger
            referanseavkastningen, og avregnes kvartalsvis.
          </p>
          <p>
            Vi benytter også et «høyvannsmerke». Det betyr at porteføljens verdi
            må innhente både eventuelt verdifall og den påløpte
            referanseavkastningen før det kan beregnes suksesshonorar.
          </p>
          <div>
            <h3 className="mb-2 font-semibold">Eksempel</h3>
            <p>
              Kunden investerer 100 kroner. Referanseavkastningen i første
              periode er 8 %, og porteføljen stiger med 10 %. Suksesshonoraret
              er 10 % av meravkastningen.
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>
                Porteføljen stiger til 110 kroner: Suksesshonoraret blir (10 −
                8) × 10 % = 0,20 kroner. Verdien etter honoraret er 109,80
                kroner, som blir utgangspunktet for neste periode.
              </li>
              <li>
                Porteføljen faller deretter til 90 kroner: Referanseavkastningen
                fortsetter å akkumuleres selv om porteføljen faller.
              </li>
              <li>
                Porteføljen stiger igjen: Dersom referanseavkastningen frem til
                neste avregning har utgjort 5 kroner, er det justerte
                høyvannsmerket 115 kroner, ikke 109,80 kroner. Bare dersom
                porteføljeverdien på avregningstidspunktet overstiger 115
                kroner, beregnes nytt suksesshonorar – og kun av beløpet over
                dette nivået.
              </li>
            </ul>
          </div>
          <p className="font-medium">
            Altså: Nytt suksesshonorar kan først beregnes når verdien overstiger
            høyvannsmerket og kravet til referanseavkastning er oppfylt.
          </p>
          <p className="italic text-muted-foreground">
            I praksis beregnes referanseavkastningen daglig, og eventuelt
            honorar avregnes kvartalsvis
          </p>
          {copyError && (
            <p role="alert" className="text-sm text-destructive">
              {copyError}
            </p>
          )}
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void copyHighWatermarkExplanation()}
            icon={explanationCopied ? <Check size={16} /> : <Copy size={16} />}
          >
            {explanationCopied ? "Kopiert" : "Kopier forklaring"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHighWatermarkOpen(false)}
          >
            Lukk
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Pricelist;
